// Activity types supported by the system
export type ActivityType = 
  | 'chat_message'
  | 'profile_update'
  | 'login'
  | 'logout'
  | 'view_dashboard'
  | 'update_settings'
  | 'chat_started'
  | 'chat_ended'

// Log levels supported by the system
export type LogLevel = 
  | 'error'
  | 'warn'
  | 'info'
  | 'debug'
  | 'trace'

interface UserContext {
  userId?: string
  userType?: 'caregiver' | 'specialist'
  sessionId?: string
}

interface ActivityContext extends LogContext {
  activityType?: ActivityType
  targetUserId?: string
  duration?: number
  status?: 'success' | 'failure'
  metadata?: Record<string, any>
}

interface LogContext {
  component?: string
  action?: string
  userContext?: UserContext
  activityContext?: ActivityContext
  [key: string]: any
}

interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  context: LogContext
}

class LoggerService {
  private static instance: LoggerService
  private logs: LogEntry[] = []
  private readonly maxLogs: number = 1000
  private userContext: UserContext = {}

  private constructor() {}

  static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService()
    }
    return LoggerService.instance
  }

  setUserContext(context: UserContext) {
    this.userContext = { ...this.userContext, ...context }
  }

  clearUserContext() {
    this.userContext = {}
  }

  private validateActivityType(type: string): type is ActivityType {
    const validTypes: ActivityType[] = [
      'chat_message',
      'profile_update',
      'login',
      'logout',
      'view_dashboard',
      'update_settings',
      'chat_started',
      'chat_ended'
    ]
    return validTypes.includes(type as ActivityType)
  }

  logActivity(activityType: string, message: string, context: Omit<ActivityContext, 'activityType'> = {}) {
    if (!this.validateActivityType(activityType)) {
      this.warn(`Invalid activity type: ${activityType}`, { invalidType: activityType })
      return
    }

    const activityContext: ActivityContext = {
      ...context,
      activityType,
      status: context.status || 'success',
      timestamp: new Date().toISOString()
    }

    this.info(message, {
      activityContext,
      userContext: this.userContext
    })
  }

  private log(level: LogLevel, message: string, context: LogContext = {}) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        userContext: {
          ...this.userContext,
          sessionId: typeof window !== 'undefined' ? sessionStorage.getItem('sessionId') : undefined
        }
      }
    }

    // Add to in-memory logs
    this.logs.push(entry)
    if (this.logs.length > this.maxLogs) {
      this.logs.shift()
    }

    // Console output with styling
    const styles = {
      debug: 'color: #808080',
      info: 'color: #0066cc',
      warn: 'color: #ff9900',
      error: 'color: #cc0000; font-weight: bold'
    }

    console.log(
      `%c[${entry.timestamp}] [${level.toUpperCase()}] ${message}`,
      styles[level],
      context
    )

    // In production, you might want to send logs to a service
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implement log shipping to a service
      // this.shipLogs(entry)
    }
  }

  debug(message: string, context: LogContext = {}) {
    this.log('debug', message, context)
  }

  info(message: string, context: LogContext = {}) {
    this.log('info', message, context)
  }

  warn(message: string, context: LogContext = {}) {
    this.log('warn', message, context)
  }

  error(message: string, context: LogContext = {}) {
    this.log('error', message, context)
  }

  getLogs(level?: LogLevel, component?: string): LogEntry[] {
    return this.logs.filter(entry => {
      if (level && entry.level !== level) return false
      if (component && entry.context.component !== component) return false
      return true
    })
  }

  clearLogs() {
    this.logs = []
  }
}

export const logger = LoggerService.getInstance()
